import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateSOPFormDTO,
  UpdateSOPFormDTO,
} from "@/modules/sop/domain/contracts";
import type {
  CreateSOPMappingDTO,
  SOPMappingQueryDTO,
  UpdateSOPMappingDTO,
} from "@/modules/sop/domain/contracts/mapping";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { sopApi, sopMappingsApi } from "./client";

export const sopKeys = {
  all: ["sop"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) => ["sop", "list", q] as const,
  detail: (id: string) => ["sop", "detail", id] as const,
};

export const sopMappingKeys = {
  all: ["sop-mappings"] as const,
  list: (q: Partial<SOPMappingQueryDTO>) =>
    ["sop-mappings", "list", q] as const,
  detail: (id: string) => ["sop-mappings", "detail", id] as const,
  bySopId: (sopId: string) => ["sop-mappings", "by-sop-id", sopId] as const,
  usage: (sopId: string) => ["sop-mappings", "usage", sopId] as const,
};

export function useSOPDetailQuery(id: string) {
  return useQuery({
    queryKey: sopKeys.detail(id),
    queryFn: () => sopApi.getById(id),
    enabled: !!id,
  });
}

export function useSOPsQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: sopKeys.list(q),
    queryFn: () => sopApi.list(q),
  });
}

export function useCreateSOP() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSOPFormDTO) => sopApi.create(input),
    onSuccess: () => {
      toast.success("ສ້າງ SOP ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: sopKeys.all });
    },
    onError: () => toast.error("ສ້າງ SOP ລົ້ມເຫຼວ"),
  });
}

export function useUpdateSOP() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateSOPFormDTO }) =>
      sopApi.update(id, input),
    onSuccess: (_, variables) => {
      toast.success("ແກ້ໄຂ SOP ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: sopKeys.all });
      qc.invalidateQueries({ queryKey: sopKeys.detail(variables.id) });
    },
    onError: () => toast.error("ແກ້ໄຂ SOP ລົ້ມເຫຼວ"),
  });
}

export function useToggleSOPStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => sopApi.toggleStatus(id),
    onSuccess: (_, id) => {
      toast.success("ປ່ຽນສະຖານະ SOP ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: sopKeys.all });
      qc.invalidateQueries({ queryKey: sopKeys.detail(id) });
    },
    onError: () => toast.error("ປ່ຽນສະຖານະ SOP ລົ້ມເຫຼວ"),
  });
}

export function useDeleteSOP() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => sopApi.delete(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: sopKeys.all });
      qc.removeQueries({ queryKey: sopKeys.detail(id) });
    },
  });

  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}

export function useDuplicateSOP() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => sopApi.duplicate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: sopKeys.all });
    },
  });

  const run = (id: string) =>
    new Promise<{ id: string; title: string }>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: (data) => resolve(data),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}

// SOP Mapping Hooks
export function useSOPMappings(query: Partial<SOPMappingQueryDTO> = {}) {
  const q: SOPMappingQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
    sopId: query.sopId,
    positionId: query.positionId,
    departmentId: query.departmentId,
    search: query.search,
  };
  return useQuery({
    queryKey: sopMappingKeys.list(q),
    queryFn: () => sopMappingsApi.list(q),
  });
}

export function useSOPMapping(id: string) {
  return useQuery({
    queryKey: sopMappingKeys.detail(id),
    queryFn: () => sopMappingsApi.get(id),
    enabled: !!id,
  });
}

export function useSOPMappingsBySopId(sopId: string) {
  return useQuery({
    queryKey: sopMappingKeys.bySopId(sopId),
    queryFn: () => sopMappingsApi.getBySopId(sopId),
    enabled: !!sopId,
  });
}

export function useSOPSUsage(sopId: string) {
  return useQuery({
    queryKey: sopMappingKeys.usage(sopId),
    queryFn: () => sopMappingsApi.getSopUsage(sopId),
    enabled: !!sopId,
  });
}

export function useCreateSOPMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSOPMappingDTO) => sopMappingsApi.create(input),
    onSuccess: () => {
      toast.success("ສ້າງການຜູກ SOP ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: sopMappingKeys.all });
    },
    onError: () => toast.error("ສ້າງການຜູກ SOP ລົ້ມເຫຼວ"),
  });
}

export function useUpdateSOPMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateSOPMappingDTO }) =>
      sopMappingsApi.update(id, input),
    onSuccess: (_, variables) => {
      toast.success("ແກ້ໄຂການຜູກ SOP ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: sopMappingKeys.all });
      qc.invalidateQueries({ queryKey: sopMappingKeys.detail(variables.id) });
    },
    onError: () => toast.error("ແກ້ໄຂການຜູກ SOP ລົ້ມເຫຼວ"),
  });
}

export function useDeleteSOPMapping() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => sopMappingsApi.delete(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: sopMappingKeys.all });
      qc.removeQueries({ queryKey: sopMappingKeys.detail(id) });
    },
    onError: () => toast.error("ລຶບການຜູກ SOP ລົ້ມເຫຼວ"),
  });

  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}
